// The service options on the "altered or repaired?" step: what each button
// says, the order they come in, and which can't be picked yet.
// Approved by Calley 2026-09-14 (mockup artifact 9eb14811).
//
// Repairs no longer take their order from the booking counts: FAMILY_ORDER
// below sets it by hand (Calley, 2026-10-30). Alterations and washes are
// unchanged.
//
// ⚠️ fxrypopup has an identical copy in src/utils/serviceOptions.js. Keep the
// two the same.

/** "JUMPSUIT - WAIST IN" on a jumpsuit reads "WAIST IN". Display only: the
 *  service's saved name — which prices, receipts and reports use — is never
 *  changed. The prefix is only dropped when it is the garment's own name
 *  ("BUTTON DOWN - …" on a BUTTON DOWN SHIRT), so a shared service such as
 *  "FIX STITCHING" keeps its whole name. */
export const shortServiceName = (name, itemName) => {
  const full = String(name ?? "").trim();
  const match = full.match(/^(.+?)\s+[-–]\s+(.+)$/);
  const garment = String(itemName ?? "")
    .trim()
    .toUpperCase();
  if (match && garment && garment.startsWith(match[1].trim().toUpperCase())) {
    return match[2].trim();
  }
  return full;
};

/** Options that belong together: the name up to " W/", "WITH", "UP TO" or
 *  "(" — so the three "SHORTEN W/ … HEM" options share a family, and so do
 *  the five hole sizes. */
export const serviceFamily = (shortName) =>
  String(shortName ?? "")
    .replace(/\s*(W\/|WITH\b|UP TO\b|\().*$/i, "")
    .trim()
    .toUpperCase();

/** A size to sort by, smallest first: `UP TO 1 1/2"` → 1.5, `(1-3 IN)` and
 *  `(1 IN-3IN)` → 1, `(< 4 IN)` just under `(> 4 IN)`. null when the name has
 *  no size. */
export const serviceSize = (shortName) => {
  const text = String(shortName ?? "");
  const upTo = text.match(/UP TO\s+(?:(\d+)\s+)?(\d+)(?:\/(\d+))?/i);
  if (upTo) {
    const whole = upTo[1] ? Number(upTo[1]) : 0;
    const part = upTo[3] ? Number(upTo[2]) / Number(upTo[3]) : Number(upTo[2]);
    return whole + part;
  }
  const bound = text.match(/\(\s*([<>])\s*(\d+)/);
  if (bound) return Number(bound[2]) + (bound[1] === "<" ? -0.01 : 0.01);
  // A jacket, coat or blazer hole carries its unit inside the range:
  // "(1 IN-3IN)" is the same shape as the jeans' "(1-3 IN)".
  const range = text.match(/\(\s*(\d+)\s*(?:IN\b\s*)?[-–]/i);
  if (range) return Number(range[1]);
  return null;
};

/** Where everything not named below goes. */
export const REST_OF_THE_LIST = "*";

/**
 * The order repair families come in, set by hand (Calley, 2026-10-30).
 *
 * Before this, every family was ranked by how often it was booked. That put
 * holes at the top, because they are booked five times as often as loose
 * stitching, and it meant the order moved on its own as the booking counts
 * moved. Calley wants the list to read the way the work does: the smallest,
 * most ordinary thing first, then up through the sizes, then the structural
 * repairs, then the fasteners.
 *
 * Families, not services. "FIX HOLE" covers all three sizes, and the size
 * sort inside a family already runs smallest first, so holes come out <1 in,
 * 1 to 3 in, >3 in without being listed one by one.
 *
 * The chainstitch lines are named here too (Calley, 31 Oct 2026). They have
 * no booking history to be ranked by and the counter reads them in a fixed
 * order: the small design first because it is the one most people pick, then
 * a name or initials, then the large design. Opening the garment up is not
 * here at all: it is an add-on, never a line anyone picks (#936).
 *
 * `REST_OF_THE_LIST` is where everything unnamed lands, still ordered by
 * bookings among itself, the way the whole list used to be. It sits where it
 * does so that buttons stay last whatever else a garment carries. Alterations
 * and washes are all unnamed and so keep exactly the order they had; they are
 * drawn in their own lists anyway, filtered by task after this runs.
 *
 * Sweater holes ("FIX ONE HOLE") are deliberately not named. They would be
 * separated from their own "ADDITIONAL FIX ONE HOLE" rows, which the approved
 * mockup keeps together, and sweaters have their own screen regardless
 * (utils/sweaterScreen.js).
 */
export const FAMILY_ORDER = [
  "FIX STITCHING",
  "FIX RIP ALONG THE SEAM",
  "FIX HOLE",
  "CROTCH REINFORCEMENT",
  "FULL CROTCH REBUILD",
  "FIX LOOSE HEM",
  "ZIPPER REPLACEMENT",
  "ELASTIC REPLACEMENT",
  REST_OF_THE_LIST,
  "REATTACH BUTTON",
  "BUTTON REPLACEMENT",
  // Chainstitch. Only the counter ever sees these (they are studio_only),
  // and they are drawn in their own list, so what matters is the order they
  // come in among themselves.
  "CHAINSTITCH - SMALL DESIGN",
  "CHAINSTITCH - NAME OR INITIALS",
  "CHAINSTITCH - LARGE DESIGN",
];

const RANK = new Map(FAMILY_ORDER.map((family, index) => [family, index]));
const REST_RANK = RANK.get(REST_OF_THE_LIST);

/** Where a family sits in FAMILY_ORDER, or the rest-of-the-list slot. */
export const familyRank = (family) => {
  const rank = RANK.get(String(family ?? "").toUpperCase());
  return rank === undefined ? REST_RANK : rank;
};

/** FAMILY_ORDER first; everything it doesn't name falls into its
 *  `REST_OF_THE_LIST` slot, most-booked family first (the backend sends
 *  `bookings`, the last 12 months). Within a family, smallest size first,
 *  then most booked, then by name. Returns a new array. */
export const orderServices = (services, itemName) => {
  const rows = (services || []).map((service) => {
    const short = shortServiceName(service?.name, itemName);
    return {
      service,
      short,
      family: serviceFamily(short),
      size: serviceSize(short),
      bookings: Number(service?.bookings) || 0,
    };
  });
  const familyTotal = new Map();
  for (const row of rows) {
    familyTotal.set(
      row.family,
      (familyTotal.get(row.family) || 0) + row.bookings,
    );
  }
  return rows
    .sort((a, b) => {
      const rankA = familyRank(a.family);
      const rankB = familyRank(b.family);
      if (rankA !== rankB) return rankA - rankB;
      // Two different families sharing the rest-of-the-list slot: the old
      // rule still decides which of them comes first. A named rank belongs
      // to one family only, so this can't fire for those.
      if (a.family !== b.family) {
        return (
          familyTotal.get(b.family) - familyTotal.get(a.family) ||
          a.family.localeCompare(b.family)
        );
      }
      return (
        (a.size ?? Infinity) - (b.size ?? Infinity) ||
        b.bookings - a.bookings ||
        a.short.localeCompare(b.short)
      );
    })
    .map((row) => row.service);
};

const ADDITIONAL = /\bADDITIONAL\s+/i;

/**
 * The denim hole rows that no longer have an each-extra price (Calley,
 * 31 Oct 2026). Every hole on a pair of jeans, or on anything else priced at
 * jeans prices, is charged at its own size's price, however many there are,
 * which is the rule sweaters already follow.
 *
 * The three JEANS - FIX ADDITIONAL RIP/HOLE services are being taken off the
 * menu (fxrybackend/scripts/retire-jeans-hole-extras.js), after which there is
 * nothing here to find. This stays as the rule rather than leaning on the
 * catalogue being empty, so a row typed back in by hand cannot start pricing
 * second holes down again.
 *
 * The blazer's buttonholes and the puffer's quilted sections still pair.
 */
const RETIRED_EACH_EXTRA = /\bADDITIONAL\b.*\bRIP\s*\/\s*HOLE\b/i;

export const hasNoEachExtra = (name) =>
  RETIRED_EACH_EXTRA.test(String(name ?? ""));

/**
 * The family a first-one price and its each-extra share. Only the lock below
 * uses it — the button order stays exactly as the approved mockup has it,
 * with all the first-one prices before all the extras.
 *
 * The catalogue words its four pairs three different ways, so both marker
 * words have to come out:
 *
 *   SWEATER - FIX ONE HOLE UP TO 1/4"  / SWEATER - ADDITIONAL FIX ONE HOLE …
 *   JEANS - FIX RIP/HOLE (<1 IN)       / JEANS - FIX ADDITIONAL RIP/HOLE …
 *   BLAZER - ADD FIRST BUTTONHOLE      / BLAZER - ADD ADDITIONAL BUTTONHOLE
 *   PUFFER JACKET - QUILTED SECTION …  / PUFFER JACKET - ADDITIONAL QUILTED …
 */
/** The short name with the pair marker taken out, so a first-one and its
 *  each-extra reduce to the same string, size and all. */
export const stripPairMarker = (shortName) =>
  String(shortName ?? "")
    .replace(/\b(ADDITIONAL|FIRST)\s+/i, "")
    .trim();

export const pairFamily = (shortName) =>
  serviceFamily(stripPairMarker(shortName));

/**
 * The services a single garment really does carry more than one of, so the
 * "How many?" control on the counter's service screen shows on those and
 * nothing else. 81 of the 210 live services.
 *
 * Calley named five on 24 Oct 2026 ("reattach button, add snaps, replace
 * buttons, fix stitching and holes"). The other four come from counting every
 * job we have ever written: 568 garments carried the same service more than
 * once, and 564 of those 568 were rung up in a single checkout, so this is
 * staff walking back through this screen rather than work added later.
 *
 *   holes                   265 garments     rips in the body   20
 *   reattach button          63              seam rips          18
 *   loose stitching          53              zippers            13
 *   buttons replaced         39              buttonholes         6
 *   snaps and hooks           9
 *
 * Seam rips and body rips are the clearest of the four: a jacket's
 * FIX HOLE/RIP already counted while its FIX RIP ALONG THE SEAM did not, and
 * the two are the same job. Every repeated zipper we read was real work on one
 * garment ("Both hip zippers", two leg zips, two pocket zips). A sleeve takes
 * four buttonholes, and their first/extra pair prices and renames itself
 * correctly through repricePairs.
 *
 * Read off the name because the catalog has no flag for it, and the wordings
 * are fixed. Left off on purpose, having been looked at:
 *
 *   - Hems and taking in. 36 and 31 garments, against 2,728 and 1,149 that
 *     carried them once. 1 to 3 per cent, and the ones we read were a job
 *     entered twice or redone, not two hems on one pair of pants.
 *   - Rips at the crotch, and any line worded TWO (FIX TWO RIPS, ADD TWO
 *     ELBOW PATCHES). There is one crotch, and a line that says two is
 *     already a pair.
 *   - Patches. 3 garments, all puffer jackets.
 *   - CHAINSTITCH, a drawn design and not stitching to fix, so FIX STITCHING
 *     is matched whole rather than on STITCH. Likewise SHORTEN SLEEVES WITH
 *     VENT BUT NO BUTTONHOLES, an alteration that happens to say the word.
 *
 * Sweaters never reach this screen: they have their own (utils/sweaterScreen
 * .js), which already takes one row per hole with its own size and place.
 */
const REPEATS_ON_ONE_PIECE = [
  /\bREATTACH\s+BUTTON\b/i,
  /\bBUTTON\s+REPLACEMENT\b/i,
  /\bSNAP\b/i,
  /\bFIX\s+STITCHING\b/i,
  /\bHOLE\b/i,
  /\bRIP\s+ALONG\s+THE\s+SEAM\b/i,
  /\bRIP\s+IN\s+MAIN\s+MATERIAL\b/i,
  /\bZIPPER/i,
  /\bADD\s+(FIRST\s+|ADDITIONAL\s+)?BUTTONHOLE\b/i,
];

export const repeatsOnOnePiece = (name) => {
  const text = String(name ?? "");
  return REPEATS_ON_ONE_PIECE.some((pattern) => pattern.test(text));
};

/**
 * What kind of work a service is: "alteration", "repair", "wash" or
 * "chainstitch".
 *
 * Mirrors fxrybackend/helper/serviceKind.js, which has the full story. The
 * short version: `service_type` is a Boolean with room for only two kinds, so
 * a wash carries `service_kind` instead. Services saved before 2026-09-18 have
 * no `service_kind` and are read from the Boolean exactly as before. An
 * unrecognised kind falls back rather than being trusted, so a typo in the
 * database can't invent a kind that no picker offers.
 */
export const serviceKind = (service) => {
  const kind = String(service?.service_kind ?? "")
    .trim()
    .toLowerCase();
  if (
    kind === "alteration" ||
    kind === "repair" ||
    kind === "wash" ||
    kind === "chainstitch"
  ) {
    return kind;
  }
  return service?.service_type === true ? "alteration" : "repair";
};

/** The task a kind sits under in the picker.
 *
 *  Chainstitch is counter-only (`studio_only` in schema/services.js), so
 *  fxryshop never receives one of these services to file under it — the
 *  backend filters them out of every garment read. The entry is here anyway,
 *  in both copies, because these two files have to stay identical and because
 *  a kind with no task would silently drop the service rather than show it in
 *  the wrong place. */
export const TASK_FOR_KIND = {
  alteration: "Alteration",
  repair: "Repairing",
  wash: "Wash",
  chainstitch: "Chainstitch",
};

/** The reverse of TASK_FOR_KIND: the kind a picked task writes.
 *
 *  Custom work ("Not on the list? Write it in") has to store a kind too, and
 *  it used to be written as a ternary on "Alteration", which quietly filed a
 *  custom wash as a repair. Deriving it means a task can never be added to the
 *  picker without the kind following. */
export const KIND_FOR_TASK = Object.fromEntries(
  Object.entries(TASK_FOR_KIND).map(([kind, task]) => [task, kind]),
);

/** True when this garment already has work booked on it that isn't a wash. */
const hasNonWashWork = (basketServices, itemId) =>
  (basketServices || []).some(
    (other) =>
      String(other?.item_details?.id ?? "") === String(itemId ?? "") &&
      serviceKind(other) !== "wash",
  );

/**
 * Why an option can't be picked yet, or null.
 *
 * An each-extra option — the sweater's "ADDITIONAL FIX ONE HOLE UP TO 1/4"",
 * the jeans' "FIX ADDITIONAL RIP/HOLE (<1 IN)", a second buttonhole, a
 * puffer's second quilted section — is the price of the one after the first,
 * so it needs a first one of the same pair family already in the basket for
 * this same garment (Calley, 2026-09-14; all four families 2026-09-18).
 *
 * A wash is answered first: it is an add-on to any work, not to a family.
 *
 * `basketServices` is the cart's services; each carries the garment it's for
 * in `item_details.id`. `services` is the garment's whole list, used only to
 * name what they need: a family with exactly one first-one price is named
 * outright, and one with five (the sweater's hole sizes) names the family.
 */
export const lockedReason = (
  service,
  { itemName, itemId, basketServices, services },
) => {
  // A wash is an add-on, never the reason a garment comes in (Calley,
  // 2026-09-18), so it needs an alteration or repair already in the basket for
  // the same garment. Same shape of rule as ADDITIONAL below, different
  // reason: both are prices that only make sense on top of something else.
  if (serviceKind(service) === "wash") {
    return hasNonWashWork(basketServices, itemId)
      ? null
      : "Add the alteration or repair first. A wash goes on top of the work we're doing.";
  }
  const short = shortServiceName(service?.name, itemName);
  if (!ADDITIONAL.test(short)) return null;
  const family = pairFamily(short);
  const isFirstOf = (name) => {
    const otherShort = shortServiceName(name, itemName);
    return !ADDITIONAL.test(otherShort) && pairFamily(otherShort) === family;
  };
  const hasFirst = (basketServices || []).some(
    (other) =>
      String(other?.item_details?.id ?? "") === String(itemId ?? "") &&
      isFirstOf(other?.name),
  );
  if (hasFirst) return null;
  const firsts = (services || []).filter((other) => isFirstOf(other?.name));
  const name =
    firsts.length === 1 ? shortServiceName(firsts[0].name, itemName) : family;
  const label = name.charAt(0) + name.slice(1).toLowerCase();
  // "Pick", not "Add": the buttonhole's own first-one price is called "ADD
  // FIRST BUTTONHOLE", and "Add “Add first buttonhole” first" stutters.
  return `Pick “${label}” first. Additional ones can be added once it’s in your basket.`;
};

/**
 * The first-one and each-extra pair a service belongs to, or null when it
 * belongs to none.
 *
 * Matched on SIZE, not family: `pairFamily` deliberately collapses the
 * sweater's five hole sizes into one family so any first hole unlocks any
 * additional one, but a 1" hole's each-extra price is its own, not the
 * 1/4" one's. So the twin is found on the short name with ADDITIONAL or
 * FIRST taken out, which keeps the size.
 *
 * Carried onto the cart row when the service is added, so the cart can work
 * out later which row is the first one without going back to the catalogue.
 * Only the four fields that decide identity and price travel: the rest of a
 * catalogue row would sit in localStorage on every service forever.
 */
const pairFace = (service) =>
  service
    ? {
        _id: service._id ?? null,
        name: service.name,
        standard_price: service.standard_price,
        premium_price: service.premium_price,
      }
    : null;

export const pairSiblings = (service, services, itemName) => {
  const short = shortServiceName(service?.name, itemName);
  const key = stripPairMarker(short).toUpperCase();
  if (!key) return null;
  let first = null;
  let extra = null;
  for (const other of services || []) {
    if (hasNoEachExtra(other?.name)) continue;
    const otherShort = shortServiceName(other?.name, itemName);
    if (stripPairMarker(otherShort).toUpperCase() !== key) continue;
    if (ADDITIONAL.test(otherShort)) extra = extra || other;
    else first = first || other;
  }
  if (!first || !extra) return null;
  return { first: pairFace(first), extra: pairFace(extra) };
};
